/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, IForm } from 'app/shared/lib/essette-site';
import { MemberService } from 'app/cm/services/member-service';
import { CasePageContext } from 'app/cm/lib/case-page-context';
import { apiCall } from 'app/shared/lib/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { IApiActionResults, IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { getReference } from 'app/shared/models/essette-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { Subscription, EventAggregator } from 'aurelia-event-aggregator';
import { CaseChangeEvent } from 'app/cm/lib/events';
import { bindable } from 'aurelia-typed-observable-plugin';
import { caseIsReadOnly } from 'app/cm/lib/utilities';
import { getMatcherFunction, MatcherFunction } from 'app/shared/lib/matcher';
import { guid } from 'app/shared/lib/guid';
import { EssModalCustomElement } from 'app/shared/elements/ess-dialog';
import { SaveCaseDiagnoses } from 'app/cm/components/case/save-case-diagnoses';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class MemberDiagnoses extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    protected caseId: string | null = null;
    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected readonly: boolean = false;

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView })
    protected memberId: string | null = null;

    protected diagnoses: ReadonlyArray<CM.MemberDiagnosis> = [];
    private diagnosesSorting: Sorting = new Sorting();
    protected diagnosesResultCount: number = 0;
    protected diagnosesPageSize: number = 25;

    private subscriptions: Array<Subscription> = [];
    protected memberDiagnosisMatcher: MatcherFunction = getMatcherFunction('diagnosis.id');
    protected diagnosisMatcher: MatcherFunction = getMatcherFunction('id');

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $diagnosesPager!: EssPagerCustomElement;
    protected $modal!: EssModalCustomElement;
    protected $saveCaseDiagnoses!: SaveCaseDiagnoses;
    protected $form!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly service: MemberService,
        private readonly context: CasePageContext,
        private readonly events: EventAggregator,
        private readonly toaster: Toaster,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!this.caseId) throw new Error('Missing or invalid caseId parameter');

        this.reload();

        this.subscriptions.push(
            this.events.subscribe(CaseChangeEvent, (e: CaseChangeEvent) => this.handleCaseChangeEvent(e))
        );
    }

    protected detached(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ***************************************************////
    protected async applyDiagnosesSort(event: SortChangeEvent): Promise<void> {
        const { property, direction } = event.detail;
        const { diagnosesSorting } = this;
        if(direction === null) {
            diagnosesSorting.remove(property);
        } else {
            diagnosesSorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        await this.loadMemberDiagnoses();
    }

    protected async handleCaseChangeEvent(e: CaseChangeEvent) {
        if(e.case && e.case.id === this.caseId) {

            await this.accountContext.load();
            this.readonly = caseIsReadOnly(e.case, this.env.user.id) || this.readonly;
            this.reload();
        };
    }

    protected async handleSaveDiagnosesFormSuccess(): Promise<void> {
        this.modalClose();
        await this.reload();
    }

    ////****************** Functions ***********************************************////
    protected addDiagnoses() {
        if(!this.caseId) throw new Error('Missing or invalid caseId parameter');
        this.$saveCaseDiagnoses.reload(this.caseId);
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    public async reload(): Promise<void> {
        if(!this.caseId) return;

        if(!this.memberId && this.context.case.id === this.caseId && this.context.case.member?.id) {
            this.memberId = this.context.case.member.id;
        } else if(!this.memberId) {
            await this.context.loadCase(this.caseId, ['member'], ['member.id']);
            this.memberId = this.context.case.member?.id || null;
        }

        await this.loadMemberDiagnoses();
    }

    protected cancel() {
        this.clear();
    }

    protected clear() {
        this.$saveCaseDiagnoses.searchResults.splice(0);
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberDiagnoses(): Promise<void> {
        if(!this.memberId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.diagnosesPageSize,
            page: this.$diagnosesPager?.page || 0,
            sorting: this.diagnosesSorting
        };

        const includes: string[] = [
            'diagnosis',
            'metadata.created.byUser'
        ];

        const fields: string[] = [
            'diagnosis.code',
            'diagnosis.description',
            'isPrincipal',
            'metadata.created.byUser.description',
            'metadata.created.timestamp'
        ];

        try {
            this.state.beginLoad();
            const response = await this.service.loadDiagnosesByMember(this.memberId, options, includes, fields);
            this.diagnoses = response.results;
            this.diagnosesResultCount = response.count || 0;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async deleteDiagnosis(id: string): Promise<void> {
        const result = await confirmation.showDelete('cm:memberDiagnosis.$long');
        if(result.dialogResponse === confirmation.options.CANCEL) return;

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.service.deactivateMemberDiagnosis(id);
            isSuccessful = response.isSuccessful;
            await this.loadMemberDiagnoses();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.deleted('cm:memberDiagnosis.$long', isSuccessful);
            this.state.endSave();
        }
    }

    @apiCall
    public async flagPrincipal(diagnosis: CM.MemberDiagnosis) {
        if(!(diagnosis.isActive && this.memberId)) return;

        await this.setPrincipalDiagnosis(diagnosis, true);
    }

    @apiCall
    public async unflagPrincipal(diagnosis: CM.MemberDiagnosis) {
        if(!(diagnosis.isActive && this.memberId)) return;

        await this.setPrincipalDiagnosis(diagnosis, false);
    }

    @apiCall
    private async setPrincipalDiagnosis(diagnosis: CM.MemberDiagnosis, isPrincipal: boolean) {
        diagnosis.isPrincipal = isPrincipal;

        try {
            this.state.beginSave();
            const response = await this.service.updateMemberDiagnosis(diagnosis);
            this.toaster.standard.saved('cm:memberDiagnosis.$long', response.isSuccessful);

            this.loadMemberDiagnoses();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }
}
